import { Injectable, effect, inject } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { DateAdapter } from "@angular/material/core";
import {
    ATOM_DATE_FORMATS,
    ATOM_DATE_FORMATS_DEFAULT,
    AtomDatepickerIntl,
    deriveDateFormatPlaceholder,
} from "@atomchat-io/ui-design-system";
import { TranslocoService } from "@jsverse/transloco";
import { toAngularLocale } from "@shared/utils/angular-locale.util";
import { take } from "rxjs";

/**
 * Localizes the `atom-date-picker` labels through Transloco: the DS footer
 * buttons (Cancel / Apply) plus the inherited `MatDatepickerIntl` calendar
 * aria-labels (open/close, prev/next month·year, view switches, range dates).
 * Extends `AtomDatepickerIntl` and re-emits `changes` so mounted pickers
 * refresh their labels on every language change.
 *
 * It is also the single owner of the app date locale: on every language change
 * it calls `DateAdapter.setLocale()` and re-derives `dateFormatPlaceholder`
 * (the DS computes it once, at construction) so the displayed value and the
 * placeholder always share the same day/month order. Components must not call
 * `DateAdapter.setLocale()` on the root adapter.
 *
 * The `formatYearRange` / `formatYearRangeLabel` methods are left as their
 * Material defaults — they only join the two dates and carry no translatable
 * text.
 *
 * Custom range validation in `atom-date-range-filter` ("Enter both start and
 * end dates", min/max bounds, etc.) is **not** part of `AtomDatepickerIntl`;
 * wire it globally via {@link provideAtomDateRangeFilterErrorMessages} and
 * `global_dropdown.calendar_filter.errors` in Transloco.
 */
@Injectable({
    providedIn: "root",
})
export class AtomDatepickerIntlService extends AtomDatepickerIntl {
    private readonly translocoService = inject(TranslocoService);
    private readonly dateAdapter = inject<DateAdapter<unknown>>(DateAdapter, {
        optional: true,
    });
    private readonly dateFormats =
        inject(ATOM_DATE_FORMATS, { optional: true }) ??
        ATOM_DATE_FORMATS_DEFAULT;
    private readonly activeLang = toSignal(this.translocoService.langChanges$);

    constructor() {
        super();
        effect(() => {
            const lang = this.activeLang();
            if (lang) {
                this.updateDateLocale(toAngularLocale(lang));
            }
            this.updateLabels();
        });
    }

    private updateDateLocale(locale: string): void {
        this.dateAdapter?.setLocale(locale);
        this.dateFormatPlaceholder = deriveDateFormatPlaceholder(
            this.dateFormats.display.dateInput,
            locale,
        );
    }

    private updateLabels(): void {
        this.translocoService
            .selectTranslateObject("atom_date_picker")
            .pipe(take(1))
            .subscribe((data) => {
                this.cancelLabel = data.cancel_label;
                this.applyLabel = data.apply_label;
                this.calendarLabel = data.calendar_label;
                this.openCalendarLabel = data.open_calendar_label;
                this.closeCalendarLabel = data.close_calendar_label;
                this.prevMonthLabel = data.prev_month_label;
                this.nextMonthLabel = data.next_month_label;
                this.prevYearLabel = data.prev_year_label;
                this.nextYearLabel = data.next_year_label;
                this.prevMultiYearLabel = data.prev_multi_year_label;
                this.nextMultiYearLabel = data.next_multi_year_label;
                this.switchToMonthViewLabel = data.switch_to_month_view_label;
                this.switchToMultiYearViewLabel =
                    data.switch_to_multi_year_view_label;
                this.startDateLabel = data.start_date_label;
                this.endDateLabel = data.end_date_label;
                this.comparisonDateLabel = data.comparison_date_label;
                this.changes.next();
            });
    }
}
